import {
  createContext,
  ReactElement,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import axios, { AxiosInstance } from 'axios';
import { Redirect, Route, RouteProps } from 'react-router';
import { api } from '@success/forms';
import i18n from '@/translation/i18n';

interface SessionData {
  customer_base_url: string;
  company_name: string;
  lng: string;
}

const apiInstance = api as AxiosInstance;

const AuthContext = createContext<{ isLoggedIn: boolean | undefined }>({
  isLoggedIn: undefined,
});

interface AxiosWrapperProps {
  children: ReactElement;
}

export function AuthProvider({ children }: AxiosWrapperProps) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>();
  const [session, setSession] = useState<SessionData | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLng = params.get('lng');
    const localSession = JSON.parse(
      localStorage.getItem('formoso_session') || '{}',
    );

    if (urlLng) {
      const updated = { ...localSession, lng: urlLng };
      localStorage.setItem('formoso_session', JSON.stringify(updated));
      return updated as SessionData;
    }

    return localSession.lng ? (localSession as SessionData) : null;
  });


  useLayoutEffect(() => {
    axios
      .get('/auth/me')
      .then((response) => {
        const { customer_base_url, company_name, lng } = response.data;

        if (!customer_base_url) {
          setIsLoggedIn(false);
          return;
        }

        const currentLng =
          session?.lng ||
          JSON.parse(localStorage.getItem('formoso_session') || '{}')
            .lng ||
          'pt';

        const sessionData = { customer_base_url, company_name, lng: currentLng };
        setSession(sessionData);

        localStorage.setItem(
          'formoso_session',
          JSON.stringify(sessionData),
        );

        [apiInstance, axios].forEach((instance) => {
          instance.defaults.baseURL = customer_base_url.slice(0, -1);
          instance.defaults.withCredentials = true;
        });

        i18n.changeLanguage(currentLng);
        setIsLoggedIn(true);
      })
      .catch(() => {
        localStorage.removeItem('formoso_session');
        setIsLoggedIn(false);
      });
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn }}>
      {isLoggedIn !== undefined && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function ProtectedRoute({ children, ...rest }: RouteProps) {
  const { isLoggedIn } = useAuth();

  return (
    <Route
      {...rest}
      render={({ location }) =>
        isLoggedIn ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/error',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

export function PublicOnlyRoute({ children, ...rest }: RouteProps) {
  const { isLoggedIn } = useAuth();

  return (
    <Route
      {...rest}
      render={({ location }) =>
        isLoggedIn === false ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}
